import { useEffect, useCallback } from 'react';
import { useGame } from '../../contexts/GameContext';
import { useStats } from '../../contexts/StatsContext';
import { useSettings } from '../../contexts/SettingsContext';
import { gameAPI } from '../../services/api';

function GameController() {
  const { state, dispatch } = useGame();
  const { recordGame } = useStats();
  const { settings } = useSettings();

  // Hard mode validation
  const validateHardMode = useCallback((guess) => {
    if (!settings.hardMode) return null; // Not in hard mode

    const { evaluations, guesses } = state;

    // Check all previous guesses for revealed hints
    for (let i = 0; i < evaluations.length; i++) {
      const evaluation = evaluations[i];
      const previousGuess = guesses[i];

      for (let j = 0; j < evaluation.length; j++) {
        const status = evaluation[j];
        const letter = previousGuess[j];

        // Rule 1: Correct (green) letters must be in the same position
        if (status === 'correct') {
          if (guess[j] !== letter) {
            return `${j + 1}${j === 0 ? 'st' : j === 1 ? 'nd' : j === 2 ? 'rd' : 'th'} letter must be ${letter}`;
          }
        }

        // Rule 2: Present (yellow) letters must be used somewhere
        if (status === 'present') {
          if (!guess.includes(letter)) {
            return `Guess must contain ${letter}`;
          }
        }
      }
    }

    return null; // Validation passed
  }, [settings.hardMode, state]);

  const handleSubmitGuess = useCallback(async () => {
    const { currentGuess, guesses, gameNumber, gameState } = state;

    // Validations
    if (gameState !== 'playing') return;

    if (currentGuess.length !== 6) {
      dispatch({ type: 'SET_ERROR', message: 'Not enough letters' });
      setTimeout(() => dispatch({ type: 'CLEAR_ERROR' }), 2000);
      return;
    }

    // Hard mode validation
    const hardModeError = validateHardMode(currentGuess);
    if (hardModeError) {
      dispatch({ type: 'SET_ERROR', message: hardModeError });
      setTimeout(() => dispatch({ type: 'CLEAR_ERROR' }), 2000);
      return;
    }

    try {
      // Validate word exists
      const validationResponse = await gameAPI.validateWord(currentGuess);

      if (!validationResponse.valid) {
        dispatch({ type: 'SET_ERROR', message: 'Not in word list' });
        setTimeout(() => {
          dispatch({ type: 'CLEAR_ERROR' });
        }, 1500);
        return;
      }

      // Check guess against target
      const checkResponse = await gameAPI.checkGuess(currentGuess, gameNumber);

      const evaluation = checkResponse.evaluation;
      const isWinner = checkResponse.is_winner;
      const isLastGuess = guesses.length === 6; // 7th guess (0-indexed)

      let newGameState = 'playing';
      let targetWord = null;

      if (isWinner) {
        newGameState = 'won';
        targetWord = checkResponse.word;
        recordGame(gameNumber, true, guesses.length + 1);
      } else if (isLastGuess) {
        newGameState = 'lost';
        recordGame(gameNumber, false, guesses.length + 1);
      }

      dispatch({
        type: 'SUBMIT_GUESS',
        evaluation,
        gameState: newGameState,
        targetWord
      });

    } catch (error) {
      console.error('Error submitting guess:', error);

      // Check if it's a game number mismatch error
      if (error.response?.data?.error?.includes('mismatch')) {
        dispatch({ type: 'SET_ERROR', message: 'Game state out of sync. Please refresh the page.' });
        return;
      }

      // Check for network/timeout errors (backend might be waking up)
      if (!error.response || error.code === 'ECONNABORTED' || error.message?.includes('Network')) {
        dispatch({ type: 'SET_ERROR', message: 'Server is waking up. Please wait a moment and try again.' });
        setTimeout(() => dispatch({ type: 'CLEAR_ERROR' }), 5000);
        return;
      }

      // Show the actual error message if available
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          'Network error. Please try again.';

      dispatch({ type: 'SET_ERROR', message: errorMessage });
      setTimeout(() => dispatch({ type: 'CLEAR_ERROR' }), 3000);
    }
  }, [state, dispatch, recordGame, validateHardMode]);

  const handleKeyPress = useCallback((key) => {
    if (state.gameState !== 'playing') return;

    if (key === 'Enter') {
      handleSubmitGuess();
    } else if (key === 'Backspace') {
      dispatch({ type: 'REMOVE_LETTER' });
    } else if (/^[a-zA-Z]$/.test(key)) {
      dispatch({ type: 'ADD_LETTER', letter: key.toUpperCase() });
    }
  }, [state.gameState, dispatch, handleSubmitGuess]);

  // Set up keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if modifier keys are pressed
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Prevent default for game keys
      if (e.key === 'Enter' || e.key === 'Backspace' || /^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
        handleKeyPress(e.key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress]);

  // Handle submit attempt from virtual keyboard
  useEffect(() => {
    const handleSubmitAttempt = (e) => {
      if (e.detail === 'SUBMIT_ATTEMPT') {
        handleSubmitGuess();
      }
    };

    window.addEventListener('submitAttempt', handleSubmitAttempt);
    return () => window.removeEventListener('submitAttempt', handleSubmitAttempt);
  }, [handleSubmitGuess]);

  // This component doesn't render anything
  return null;
}

export default GameController;